import { createTheme } from '@mui/material/styles'
import baseThemeDefs from '@/themes/base'
import { DIST_PRODUCT } from '@/enums/dist-products'
import { ColorModes } from '@/modules/layout/constants'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { accordionClasses } from '@mui/material/Accordion'
import { alertClasses } from '@mui/material/Alert'
import ThemeInterface, { ThemeConfig } from './theme-interface'

// Recursive partial — used to type product overrides (e.g. the MeinBüro delta)
// that only specify the leaves that differ from this base theme.
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? DeepPartial<U>[]
    : T[P] extends object
      ? DeepPartial<T[P]>
      : T[P]
}

// Both flags are derived from the build target so they are correct regardless of
// which product entry (theme.ts / theme.meinbuero.ts) re-exports this base.
const isOrgaMax = ENV.product === DIST_PRODUCT.orgamax
const isMeinbuero = ENV.product === DIST_PRODUCT.meinbuero
const primary = '#1c7bf1'
const primaryV2 = '#275FE0'
const accent = '#ee751c'

export const FONT_SIZES = Object.freeze({
  micro: '8px',
  tinier: '9px',
  tiny: '10px',
  tooltip: '11px',
  extrasmall: '10px',
  smaller: '12px',
  small: '13px',
  default: '14px',
  smedium: '16px',
  medium: '18px',
  large: '20px',
  xlarge: '24px',
  xxlarge: '28px',
  giant: '32px',
  huge: '36px',
  xhuge: '40px',
})

const BASE_COLORS = Object.freeze({
  white: '#ffffff',
  black: '#414a5f',
  red: '#e9516d',
  green: '#5eb53b',
  grey: '#cccccc',
  realBlack: '#000000',
  yellow: '#e1b757',
})

export const FONT_WEIGHTS = Object.freeze({
  thin: 100,
  extraLight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
})

const FONT_COLORS = Object.freeze({
  text: '#414a5f',
  secondary: '#647190',
})

export const LINE_HEIGHTS = Object.freeze({
  teaserHeadline: '1.67',
  registerHeadline: '1.5',
  registerBullets: '1.375',
})

const PRIMARY_COLORS = Object.freeze({
  primary,
  primaryLight: '#4c83ff',
  primaryBright: '#4277FC',
  primarySuperLight: '#AED0F6',
  primaryDark: '#3264ff',
})

const ACCENT_COLORS = Object.freeze({
  accent,
  accentLight: '#f58837',
  accentSuperLight: '#faac71',
  accentDark: '#ee751c',
})

const ONBOARDING_COLORS = Object.freeze({
  black1: '#19233c',
  grey1: '#979797',
  grey2: '#c0c2c8',
  grey3: '#282828',
})

const REGISTER_COLORS = Object.freeze({
  bulletIcon: '#4277FC',
})

const BORDER_COLORS = Object.freeze({
  default: '#dee2ed',
  light: '#e1e3ed',
  medium: '#dee2ed',
  defaultActive: '#7599e9',
  transactionColumn: '#e3e3e3',
  outline: '#275FE0',
  dangerLight: '#f5c8c8',
})

// Figma `_components/alert/*` - the standard (non-filled) MUI Alert surfaces. Product independent:
// an alert states a severity, it does not carry the brand color.
const ALERT_COLORS = Object.freeze({
  infoBackground: '#CDE7F6',
  infoText: '#023654',
  infoIcon: '#0288D1',
})

const BANK_COLORS = Object.freeze({
  sparkasse: '#e60005',
  commerzbank: '#fedf33',
  deutscheBank: '#1c62b7',
  volksbank: '#0a438a',
  postbank: '#010166',
  paypal: '#253b80',
  n26: '#36a18a',
  ingDiba: '#ff6600',
})

const CHECKBOX_COLORS = Object.freeze({
  label: '#023e84',
  text: '#7b7b7b',
})

const ICON_COLORS = Object.freeze({
  icon: '#4b79f3',
  controlButton: '#757b8a',
  fillColor: '#414A5F',
})

// The shared base theme (OrgaMax defaults). Product variants deep-merge a delta
// onto this object; see theme.meinbuero.ts.
export const baseCustomTheme = {
  ...baseThemeDefs,
  primary,
  primaryV2,
  primaryLight: PRIMARY_COLORS.primaryLight,
  primaryBright: PRIMARY_COLORS.primaryBright,
  primarySuperLight: PRIMARY_COLORS.primarySuperLight,
  primaryDark: PRIMARY_COLORS.primaryDark,
  accent,
  accentLight: ACCENT_COLORS.accentLight,
  accentSuperLight: ACCENT_COLORS.accentSuperLight,
  accentDark: ACCENT_COLORS.accentDark,
  colorPrimaryButtonBackground: '#275FE0',
  colorPrimaryButtonBackgroundHover: '#1B4EBE',
  colorDeleteButtonBackground: '#ee4b4c',
  colorDeleteButtonBackgroundHover: '#f48888',

  backgroundLight: '#f7faff',
  backgroundDarkBlue: '#161e37',
  lighterGrey: '#f2f3f5',
  lightGrey: '#aaa',
  midGrey: BASE_COLORS.grey,
  midLightGrey: '#e5e5e5',
  superlightGrey: '#f7f7f7',
  ultralightGrey: '#f6f7fa',
  darkGrey: '#808080',
  darkestGrey: '#615b5b',
  darkerGrey: '#878787',
  midDarkerGrey: '#8c929c',
  red: '#e9516d',
  redBackground: 'rgba(233, 81, 109, 0.15)',
  green: '#89c958',
  greenBackground: 'rgba(137, 201, 88, 0.15)',
  green2: '#a2c62e',
  lightGreen: '#e8ffc7',
  darkerGreen: '#1B6F14',
  orange: '#e8b640',
  orangeBackground: 'rgba(232, 182, 64, 0.15)',
  darkGreen: '#4ca107',
  blue: '#3264ff',
  darkBlue: '#06437b',
  darkerBlue: '#19437b',
  darkBlueAlt: '#1F2E53',
  lightBlue: '#739eff',
  lighterBlue: '#82abfb',
  lighterBlueBackground: 'rgba(130, 171, 251, 0.15)',
  lightBlueBackground: '#E0E8FF',
  primaryBrightBackground: '#ecf2ff',
  alternateBlueBackground: '#CDE7F6',
  brightBlue: '#4277FC',
  paleBlue: '#63849f',
  paleBluePink: '#f5f6fa',
  white: BASE_COLORS.white,
  black: BASE_COLORS.black,
  black1: ONBOARDING_COLORS.black1,
  realBlack: BASE_COLORS.realBlack,
  error: '#e73c5b',
  errorLight: '#e73c5b33',
  errorLighter: '#e73c5b1a',
  progressRed: '#d64b5e',
  errorText: '#8f2b2b',
  grey1: ONBOARDING_COLORS.grey1,
  grey2: ONBOARDING_COLORS.grey2,
  grey3: '#909ba8',
  yellowBackground: 'rgba(225, 183, 87, 0.1)',
  errorBackground: '#fff4f4',

  text: BASE_COLORS.black,
  textSecondary: '#647190',
  textLight: '#83878e',
  textLighter: '#656768',
  textMuted: '#747474',
  textPlaceholder: BASE_COLORS.grey,
  purple: '#d662e4',

  colorAlert: '#ff9191',
  colorHint: '#E0E8FF',

  disabledColor: BASE_COLORS.grey,

  activeInputBorder: BORDER_COLORS.defaultActive,
  borderOutlineColor: BORDER_COLORS.outline,

  labelLight: '#878da0',
  finalizeHeaderColor: '#255bcf',
  borderColor: BORDER_COLORS.default,
  borderColorQuickFilter: '#989ca6',
  transparentBorder: 'rgba(255, 255, 255, .1)',
  disabledBorderColor: BASE_COLORS.grey,
  transactionColumnBorder: `solid 1px ${BORDER_COLORS.transactionColumn}`,
  headerColor: '#255bcf',
  backgroundColor: '#f6f7fa',
  backgroundColorDisabled: BASE_COLORS.white,
  backgroundColorIconDisabled: '#dddddd',
  backgroundColorLight: BASE_COLORS.white,
  backgroundColorBeige: '#f2f2f2',
  backgroundColorGreen: '#E7F4DE',
  backgroundColorPrimaryLight: '#ECF1FF',
  buttonColor: BASE_COLORS.white,
  textColor: BASE_COLORS.black,
  iconColor: ICON_COLORS.icon,
  iconColorControlButton: ICON_COLORS.controlButton,
  iconFillColor: ICON_COLORS.fillColor,
  iconFillColorLight: '#76b5e4',
  redStateColor: '#fa6e6e',
  redStateColorLight: '#fa6e6ecc',
  greyStateColor: '#c6c6c6',
  rowHoverColor: '#e9edf9',
  newTagColor: '#D9D9D9',
  lightBackground: '#d9e4fe',
  cyanMint: '#C3FFF8',
  darkCyan: '#178B92',
  darkerCyan: '#005062',

  borderRadius: '4px',
  borderRadiusLarge: '8px',
  borderRadiusExtraLarge: '12px',
  borderRadiusExtraExtraLarge: '16px',
  borderRadiusPill: '999px',
  // the soft shadow of the cards in the new brand design, e.g. the steps of the checkout
  shadowCard: '0px 1px 6px 0px #15233F14',
  border: `solid 1px ${BORDER_COLORS.default}`,
  borderDashed: `dashed 1px ${BORDER_COLORS.default}`,

  icons: {
    add: '\\F102',
    plus: '\\F165',
    base: ICON_COLORS.fillColor,
  },
  colors: BASE_COLORS,
  bankColors: {
    sparkasse: BANK_COLORS.sparkasse,
    commerzbank: BANK_COLORS.commerzbank,
    deutscheBank: BANK_COLORS.deutscheBank,
    volksbank: BANK_COLORS.volksbank,
    postbank: BANK_COLORS.postbank,
    paypal: BANK_COLORS.paypal,
    n26: BANK_COLORS.n26,
    ingDiba: BANK_COLORS.ingDiba,
  },
  checkboxColors: {
    label: CHECKBOX_COLORS.label,
    text: CHECKBOX_COLORS.text,
  },

  promotionContentBackgroundColor: '#1d4299',

  // firstSteps
  firstSteps: {
    welcomeSection: {
      backgroundColorPrimary: '#141E39',
      backgroundColorPrimaryLight: '#1F2E53',
      tooltipBackgroundColor: '#414A5F',
    },
  },

  // sidebar:
  sidebarColor: '#1045b8',
  sidebarColorModal: '#141e39',
  sidebarColorModalRight: '#141e39',
  sidebarStepCircleColor: '#ccc',
  sidebarStepCircleColorActive: '#1976d2',

  // nextButton
  nextButton: '#2350dd',

  // navigation / menu
  navMenuItemBackgroundColor: '#141e39',
  navSubmenuItemColorActive: primary,
  navSubmenuImprintItemColorLight: BASE_COLORS.grey,
  navBackgroundColor: '#1f2e55',
  navTopBarBackgroundColor: '#141e39',

  // lists
  listHeaderBackgroundColor: '#f7f7f7',

  // heights
  mainHeaderHeight: '85px',

  isOrgaMax,
  isMeinbuero,
  // HorizontalRule
  horizontalRule: {
    lineColor: ONBOARDING_COLORS.grey1,
    color: ONBOARDING_COLORS.grey3,
    fontSize: FONT_SIZES.tiny,
  },
  DocumentControlButton: {
    iconColor: ICON_COLORS.controlButton,
  },
  Input: {
    borderColor: BORDER_COLORS.default,
    borderColorActive: BORDER_COLORS.defaultActive,
  },

  // UserAuthDialog
  UserAuthDialog: {
    title: {
      fontSize: FONT_SIZES.medium,
    },
    footer: {
      fontSize: FONT_SIZES.smaller,
    },
  },

  // TextPatternStrength
  TextPatternStrength: {
    listItem: {
      fontSize: '11px',
      color: ONBOARDING_COLORS.black1,
    },
    successIcon: {
      color: PRIMARY_COLORS.primaryLight,
    },
    failedIcon: {
      color: ONBOARDING_COLORS.grey2,
    },
  },

  // login, registration, password forgot and account confirmation
  auth: {
    text: {
      primary: {
        fontSize: FONT_SIZES.default,
        color: BASE_COLORS.black,
      },
      secondary: {
        fontSize: FONT_SIZES.micro,
        color: BASE_COLORS.black,
      },
    },
    link: {
      color: '#2b5fff',
    },
    border: {
      color: 'rgba(151, 151, 151, .25)',
    },
    login: {
      forgotPassword: {
        link: {
          fontSize: FONT_SIZES.tiny,
        },
      },
    },
    code: {
      description: {
        fontSize: FONT_SIZES.small,
      },
      resend: {
        fontSize: FONT_SIZES.tiny,
      },
      form: {
        placeholder: {
          color: BASE_COLORS.grey,
        },
      },
    },
    sidebar: {
      content: {
        background: {
          primaryColor: '#1045b8',
          secondaryColor: '#0C276B',
        },
        headline: {
          color: BASE_COLORS.white,
          fontSize: FONT_SIZES.xlarge,
          lineHeight: LINE_HEIGHTS.teaserHeadline,
        },
        register: {
          headline: {
            color: BASE_COLORS.white,
            fontSize: FONT_SIZES.xlarge,
            lineHeight: LINE_HEIGHTS.registerHeadline,
            textAlign: 'left',
            fontWeight: FONT_WEIGHTS.medium,
            padding: '0 0 43px 0',
            margin: 0,
          },
          bullets: {
            color: BASE_COLORS.white,
            fontSize: FONT_SIZES.smedium,
            fontWeight: FONT_WEIGHTS.light,
            lineHeight: LINE_HEIGHTS.registerBullets,
            paddingBottom: 98,
          },
          bullet: {
            paddingBottom: 20,
            '&:last-child': {
              paddingBottom: 0,
            },
          },
          bulletIcon: {
            width: 15,
            height: 15,
            marginRight: 10,
            '& > *': {
              fill: REGISTER_COLORS.bulletIcon,
            },
          },
        },
      },
      footer: {
        link: {
          fontSize: FONT_SIZES.tiny,
          color: BASE_COLORS.white,
        },
      },
    },
  },

  signature: {
    moduleModal: {
      backgroundColor: '#1f2e55',
    },
  },

  trialSelection: {
    optionCard: {
      backgroundColor: '#1f2e55',
    },
  },

  // sales stats on dashboard
  topSalesStats: {
    top1: '#5b9ff2',
    top2: PRIMARY_COLORS.primarySuperLight,
    top3: '#686868',
    top4: '#a9a9a9',
    top5: '#dbdbdb',
  },

  typography: {
    fontFamily: '\'Fira Sans\', Helvetica, Arial, sans-serif',
    body1: {
      color: FONT_COLORS.text,
    },
    hint: {
      fontSize: FONT_SIZES.smaller,
      color: FONT_COLORS.secondary,
      lineHeight: '1rem',
    },
  },
  palette: {
    mode: 'light',

    primary: {
      main: '#4277FC',
      dark: '#275FE0',
      light: '#6792FC',
      contrastText: '#FFFFFF',
      50: '#ECF1FF',
      100: '#D9E4FE',
      200: '#B3C9FE',
      300: '#E2E8FE',
      400: '#6792FC',
      500: '#4277FC',
      550: '#275FE0',
      600: '#2E53B0',
      700: '#1F2E53',
      800: '#141E39',
    },

    error: {
      main: '#EE4A4D',
      dark: '#D1484B',
      light: '#FA6E6E',
      contrastText: '#FFFFFF',
    },

    warning: {
      main: '#E8B641',
      dark: '#BA9234',
      light: '#F1D38D',
      background: '#FAF0D9',
      contrastText: '#111111',
    },

    info: {
      main: '#0288D1',
      dark: '#01579B',
      light: '#03A9F4',
      background: '#CDE7F6',
      contrastText: '#FFFFFF',
    },

    success: {
      main: '#4CAF50',
      dark: '#49A84D',
      light: '#68B96B',
      contrastText: '#FFFFFF',
    },

    customGrey: {
      main: BASE_COLORS.grey,
      light: '#414a5f',
      contrastText: BASE_COLORS.black,
    },

    // DO-13587: colors of the new brand, only used in the checkout so far
    brand: {
      main: '#2563EB',
      dark: '#1D4ED8',
      light: '#EFF6FF',
      contrastText: '#FFFFFF',
      divider: '#ECEDF0',
      // border of form fields, e.g. the e-mail code fields of the registration
      inputBorder: '#C4C7CD',
      // background of something chosen or fulfilled, e.g. a selected add-on or a met password rule
      selected: '#EAF1FB',
      error: '#EB1C1C',
      // background of promotion badges and highlights
      promotion: '#F6F0E4',
      text: {
        heading: '#070C17',
        primary: '#2A3244',
        secondary: '#52596A',
      },

    },

    amber: {
      50: '#FDF8EC',
      100: '#FAF0D9',
      200: '#F6E2B3',
      300: '#F1D38D',
      400: '#EDC567',
      500: '#E8B641',
      600: '#BA9234',
      700: '#5D491A',
      800: '#2F250D',
      A100: '#FFFCDA',
    },

    lightBlue: {
      50: '#E6F3FA',
      100: '#CDE7F6',
      200: '#9BCFED',
      300: '#6AB8E3',
      400: '#38A0DA',
      500: '#0688D1',
      600: '#056DA7',
      700: '#04527D',
      800: '#023654',
      A100: '#D5F4FF',
    },

    green: {
      50: '#F3FAEE',
      100: '#E7F4DE',
      200: '#DCEFCD',
      300: '#B8DF9B',
      400: '#A1D479',
      500: '#89C958',
      600: '#6EA146',
      700: '#527935',
      800: '#293C1A',
      A100: '#E8FFC7',
      A400: '#D1F58B',
      A700: '#1B6F14',
    },

    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      chip: '#D9DCE5',
    },

    divider: '#E0E1E4',

    text: {
      primary: '#414A5F',
      secondary: '#647190',
      disabled: 'rgba(100, 113, 144, 0.35)',
    },
  },

  fontSizes: FONT_SIZES,
  fontWeights: FONT_WEIGHTS,
  fontColors: FONT_COLORS,
  fontFamily: '\'Fira Sans\', Helvetica, Arial, sans-serif',
  // DO-13587: typefaces of the new brand, only used in the checkout so far
  brandFontFamilies: {
    heading: '\'Sora\', \'Poppins\', Helvetica, Arial, sans-serif',
    body: '\'Poppins\', Helvetica, Arial, sans-serif',
  },
  // type scale of the new brand - headings (Sora) differ on mobile, text (Poppins) does not
  brandTypography: {
    headings: {
      h1: { desktop: { fontSize: 64, lineHeight: 1.1, letterSpacing: '-0.02em' }, mobile: { fontSize: 34, lineHeight: 1.2, letterSpacing: '-0.02em' } },
      h1Small: { desktop: { fontSize: 56, lineHeight: 1.1, letterSpacing: '-0.02em' }, mobile: { fontSize: 32, lineHeight: 1.2, letterSpacing: '-0.02em' } },
      h2: { desktop: { fontSize: 48, lineHeight: 1.2, letterSpacing: '-0.02em' }, mobile: { fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.02em' } },
      h3: { desktop: { fontSize: 40, lineHeight: 1.2, letterSpacing: '-0.01em' }, mobile: { fontSize: 28, lineHeight: 1.3, letterSpacing: '-0.02em' } },
      h4: { desktop: { fontSize: 32, lineHeight: 1.3, letterSpacing: '-0.02em' }, mobile: { fontSize: 24, lineHeight: 1.3, letterSpacing: '-0.02em' } },
      h5: { desktop: { fontSize: 24, lineHeight: 1.3, letterSpacing: '-0.02em' }, mobile: { fontSize: 20, lineHeight: 1.4, letterSpacing: '-0.02em' } },
      h6: { desktop: { fontSize: 20, lineHeight: 1.4, letterSpacing: '-0.02em' }, mobile: { fontSize: 18, lineHeight: 1.4, letterSpacing: '-0.02em' } },
    },
    text: {
      large: { fontSize: 20, lineHeight: 1.5 },
      medium: { fontSize: 18, lineHeight: 1.5 },
      regular: { fontSize: 16, lineHeight: 1.5 },
      small: { fontSize: 14, lineHeight: 1.5 },
      tiny: { fontSize: 12, lineHeight: 1.5 },
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
    },
  },
  borderColors: BORDER_COLORS,

  // mobile navigation
  mobileNav: {
    demoHeader: '#4277fc',
    headerButton: '#1a4ebe',
    activeMenuItem: '#4277fc',
    inactiveMenuItem: '#f1f2f5',
  },

  // media queries
  devices: {
    mobile: '@media (max-device-width: 768px)',
    tablet: '@media (max-device-width: 992px)',
    laptop: '@media (max-device-width: 1280px)',
  },

  defaultDrawerWidth: 510,
} as unknown as ThemeConfig

// The MUI component overrides, built as a factory so the (possibly product-merged)
// theme's token values flow into the component styles.
export const buildMuiComponents = (theme: ThemeConfig) => ({
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput': {
          '&-input': {
            height: '24px',
            padding: '20px 18px 6px ',
            color: ONBOARDING_COLORS.black1,
            lineHeight: 0.83,
          },
        },
        '& legend': {
          display: 'none',
        },
        '& .MuiInputLabel-outlined': {
          transform: 'translate(14px, 16px) scale(1)',
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
          transform: 'translate(18px, 8px) scale(.75)',
          color: theme.textColor,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        '&.Mui-required': {
          color: BASE_COLORS.red,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderRadius: '6.4px',
          border: `solid 0.8px ${theme.Input.borderColor}`,
        },
        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          border: `solid 0.8px ${theme.Input.borderColorActive}`,
        },
        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
          border: `solid 0.8px ${theme.Input.borderColorActive}`,
        },
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        [`&.${accordionClasses.rounded}`]: {
          boxShadow: 'none',
          borderRadius: '4px',
          margin: '20px 0',
          border: `solid 1px ${theme.borderColor}`,
          '&:before': {
            content: 'none',
          },
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: '\'Fira Sans\', Helvetica, Arial, sans-serif;',
      },
      body1: {
        fontFamily: '\'Fira Sans\', Helvetica, Arial, sans-serif;',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: 0,
      },
      colorPrimary: {
        backgroundColor: '#f6f7fa',
        '&.Mui-checked': {
          color: theme.primary,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: FONT_SIZES.smedium,
        fontWeight: FONT_WEIGHTS.regular,
        fontFamily: '\'Fira Sans\', Helvetica, Arial, sans-serif;',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        color: theme.textColor,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderRadius: '2px',
        },
        [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.primary,
        },
        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.primary,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: '45px',
        textTransform: 'none',
        fontSize: FONT_SIZES.default,
        fontWeight: FONT_WEIGHTS.regular,
        boxShadow: 'none',
      },
      outlined: {
        backgroundColor: BASE_COLORS.white,
      },
      outlinedCustomGrey: {
        color: '#000',
        borderColor: BORDER_COLORS.default,
        '&:hover': {
          borderColor: BASE_COLORS.grey,
          backgroundColor: 'rgba(0,0,0,0.04)',
        },
      },
    },
    variants: [
      {
        props: { variant: 'tertiary' },
        style: {
          padding: '5px 15px',
          backgroundColor: BASE_COLORS.white,
          border: `1px solid ${BORDER_COLORS.default}`,
          color: BASE_COLORS.black,
          '&:hover': {
            borderColor: BORDER_COLORS.default,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    ],
  },
  MuiAlert: {
    styleOverrides: {
      standardInfo: {
        backgroundColor: ALERT_COLORS.infoBackground,
        color: ALERT_COLORS.infoText,
        [`& .${alertClasses.icon}`]: {
          color: ALERT_COLORS.infoIcon,
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        border: `solid 1px ${theme.borderColor}`,
        borderRadius: '8px',
        boxShadow: '0 10px 28px rgba(20, 30, 57, 0.14), 0 2px 8px rgba(20, 30, 57, 0.08)',
        backgroundImage: 'none',
      },
    },
  },
})

export const DARK_MODE_COLORS = Object.freeze({
  color: {
    default: BASE_COLORS.white,
    medium: '#aec7e3',
    light: '#dee2ed',
  },
  backgroundColor: {
    default: '#1f2e55',
    medium: '#141e39',
    light: PRIMARY_COLORS.primaryBright,
  },
  borderColor: {
    default: BASE_COLORS.white,
    medium: BORDER_COLORS.medium,
    light: '#727c94',
  },
  boxShadow: {
    default: '0 -5px 12px 0 rgba(0, 0, 0, 0.2)',
  },
})

export const LIGHT_MODE_COLORS = Object.freeze({
  color: {
    default: '#414a5f',
    medium: '#878da0',
    light: '#a1a5b0',
  },
  backgroundColor: {
    default: BASE_COLORS.white,
    medium: '#f6f7fa',
    light: '#e9edf9',
  },
  borderColor: {
    default: BORDER_COLORS.default,
    medium: BORDER_COLORS.medium,
    light: BORDER_COLORS.medium,
  },
  boxShadow: {
    default: '0 -5px 12px 0 rgba(0, 0, 0, 0.06)',
  },
})

type ColorModesTypes = keyof typeof ColorModes
type ModeColorSets = {
  light: typeof LIGHT_MODE_COLORS
  dark: typeof DARK_MODE_COLORS
}

// Builds the runtime MUI theme API from a (base or product-merged) token object,
// its mode color sets, and the component overrides derived from that same token
// object. Shared by both product entries so the factory logic lives in one place.
export const buildThemeApi = (customTheme: ThemeConfig, modeColors: ModeColorSets) => {
  const muiThemeOverrides = { components: buildMuiComponents(customTheme) }

  const createMuiThemeWithColorMode = (mode: ColorModesTypes = ColorModes.light): ThemeInterface => {
    let modeColorSet: ThemeConfig['mode'] = { type: ColorModes.light, ...modeColors.light }
    if (mode === ColorModes.dark) {
      modeColorSet = {
        type: ColorModes.dark,
        ...modeColors.dark,
      }
    }

    return createTheme({
      ...customTheme,
      ...muiThemeOverrides,
      mode: modeColorSet,
    } as ThemeConfig) as ThemeInterface
  }

  const muiTheme = createTheme({
    ...customTheme,
    ...muiThemeOverrides,
  } as ThemeConfig) as ThemeInterface

  return { createMuiThemeWithColorMode, muiTheme }
}

const { createMuiThemeWithColorMode, muiTheme } = buildThemeApi(baseCustomTheme, {
  light: LIGHT_MODE_COLORS,
  dark: DARK_MODE_COLORS,
})

export { createMuiThemeWithColorMode, muiTheme }

export default baseCustomTheme
